import React, { useEffect, useMemo, useState } from 'react';
import { Card, Container, Label, Spinner } from 'reactstrap';
import { useFormik } from 'formik';
import { Link, useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';

import Breadcrumb from 'components/App/Breadcrumb';
import Meta from 'components/App/Meta';
import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import AppFlatPickInput from 'components/App/FlatPickInput';
import FlightStatusBadge from 'components/App/FlightStatusBadge';
import Pagination from 'components/Pagination';
import SearchIcon from 'assets/images/search-icon.svg';
import flightBookingApi from 'api/flightBookingApi';
import ENDPOINT from 'constants/endPoint';
import {
  FLIGHT_BOOKING_STATUS_OPTIONS,
  FLIGHT_BOOKING_TAB,
  FLIGHT_SEARCH_PLACEHOLDER,
  NEEDS_ACTION_REASON_OPTIONS,
  TRIP_TYPE_LABELS,
  formatFlightAmount,
  formatFlightDateTime,
  getLoadErrorText,
  getReasonFlags,
  handleFlightApiError,
  toSystemOptions,
} from 'constants/flightBooking';
// ponytail: reuses the ManagerPayment list styles; copy them here if the pages diverge.
import pageStyles from 'pages/ManagerPayment/ManagerPayment.module.scss';

import styles from './ManagerFlightBooking.module.scss';

const PAGE_TITLE = 'Quản lý đặt vé máy bay';
const breadcrumbList = [{ name: PAGE_TITLE, isActive: true, link: '' }];

const ITEMS_PER_PAGE = 20;

const EMPTY_FILTERS = {
  q: '',
  status: '',
  system: '',
  needsActionReason: '',
  createdFrom: '',
  createdTo: '',
  departureFrom: '',
  departureTo: '',
};

const EMPTY_LIST = { data: [], total: 0 };

const HEADS = ['Mã đơn | PNR', 'Hành trình', 'Liên hệ', 'Tổng tiền', 'Trạng thái', 'Ngày tạo', 'Thao tác'];

const RANGE_OPTIONS = { mode: 'range', showMonths: 2, dateFormat: 'd/m/Y' };

const rangeLabel = (from, to) => [from, to]
  .filter(Boolean)
  .map((date) => moment(date).format('DD/MM/YYYY'))
  .join(' đến ');

const formatRoute = (legs) => (legs || [])
  .map((leg) => `${leg.startPoint} → ${leg.endPoint}`)
  .join(' | ') || '-';

function ManagerFlightBooking() {
  const history = useHistory();
  const location = useLocation();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tab = queryParams.get('tab') || FLIGHT_BOOKING_TAB.ALL;
  const page = parseInt(queryParams.get('page'), 10) || 1;
  // Read once, on mount only (a deep link from the dashboard's "Cần xử lý" reasons); like
  // EMPTY_FILTERS below, only the value passed on the very first render is ever used.
  const initialFilters = {
    ...EMPTY_FILTERS,
    needsActionReason: queryParams.get('needsActionReason') || '',
  };

  const [filters, setFilters] = useState(initialFilters);
  const [list, setList] = useState(EMPTY_LIST);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [needsActionCount, setNeedsActionCount] = useState(0);
  const [systems, setSystems] = useState([]);

  useEffect(() => {
    flightBookingApi.getFlightSystems()
      .then((response) => setSystems(response.payload || []))
      .catch(handleFlightApiError);
    flightBookingApi.getFlightBookings({ needsAction: true, limit: 1 })
      .then((response) => setNeedsActionCount(response.payload?.total || 0))
      .catch(() => setNeedsActionCount(0));
  }, []);

  useEffect(() => {
    let ignore = false;

    setIsLoading(true);
    flightBookingApi.getFlightBookings({
      ...filters,
      needsAction: tab === FLIGHT_BOOKING_TAB.NEEDS_ACTION || undefined,
      page,
      limit: ITEMS_PER_PAGE,
    })
      .then((response) => {
        if (ignore) return;
        setList(response.payload || EMPTY_LIST);
        setLoadError('');
      })
      .catch((error) => {
        if (ignore) return;
        // Never leave the previous query's rows under the new filters.
        setList(EMPTY_LIST);
        setLoadError(getLoadErrorText(error, 'danh sách đơn vé máy bay', 'Vui lòng thử lại.'));
        handleFlightApiError(error);
      })
      .finally(() => !ignore && setIsLoading(false));

    return () => { ignore = true; };
  }, [filters, tab, page]);

  const pushQuery = (changes) => {
    Object.entries(changes).forEach(([key, value]) => queryParams.set(key, value));
    history.push({ search: queryParams.toString() });
  };

  const validation = useFormik({
    initialValues: initialFilters,
    onSubmit: (formValues) => {
      setFilters({ ...formValues, q: formValues.q.trim() });
      pushQuery({ page: 1 });
    },
  });
  const { values } = validation;

  const onReset = () => {
    validation.resetForm();
    setFilters(EMPTY_FILTERS);
    pushQuery({ page: 1 });
  };

  const onSelect = (e) => validation.setFieldValue(e.name, e.value ?? '');

  const onChangeRange = (fromKey, toKey) => (dates = []) => {
    const [from = '', to = ''] = dates.map((date) => moment(date).format('YYYY-MM-DD'));
    validation.setFieldValue(fromKey, from);
    validation.setFieldValue(toKey, to);
  };

  const systemOptions = useMemo(() => toSystemOptions(systems), [systems]);
  const pageCount = Math.ceil(list.total / ITEMS_PER_PAGE) || 1;

  const tabs = [
    { value: FLIGHT_BOOKING_TAB.ALL, label: 'Tất cả' },
    { value: FLIGHT_BOOKING_TAB.NEEDS_ACTION, label: `Cần xử lý (${needsActionCount})` },
  ];

  const renderRows = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={HEADS.length} className="text-center p-4">
            <Spinner size="sm" color="primary" />
            {' '}
            Đang tải dữ liệu...
          </td>
        </tr>
      );
    }

    if (!list.data.length) {
      return (
        <tr>
          <td colSpan={HEADS.length} className="text-center p-4 text-muted">
            {loadError || 'Không tìm thấy đơn vé máy bay phù hợp'}
          </td>
        </tr>
      );
    }

    return list.data.map((booking) => (
      <tr key={booking._id}>
        <td>
          <div className={styles.Strong}>{booking.orderId}</div>
          <div className={styles.Muted}>{`PNR: ${booking.pnrs?.[0]?.bookingCode || '-'}`}</div>
        </td>
        <td>
          <div>{formatRoute(booking.legs)}</div>
          <div className={styles.Muted}>
            {`${TRIP_TYPE_LABELS[booking.tripType] || booking.tripType || '-'} · `
              + `${booking.system || '-'} · ${formatFlightDateTime(booking.legs?.[0]?.startAt)}`}
          </div>
        </td>
        <td>
          <div>{booking.contact?.name || '-'}</div>
          <div className={styles.Muted}>
            {booking.contact?.phone || booking.contact?.email || '-'}
          </div>
        </td>
        <td className={styles.Strong}>
          {formatFlightAmount(booking.pricing?.grandTotal, booking.pricing?.currency)}
        </td>
        <td>
          <FlightStatusBadge status={booking.status} />
          {booking.instant && <div className={styles.Flag}>Vé gấp</div>}
          {getReasonFlags(booking.needsActionReasons).map((label) => (
            <div key={label} className={styles.Flag}>{label}</div>
          ))}
          <div className={styles.Muted}>{`Đối tác: ${booking.partnerStatus || '-'}`}</div>
        </td>
        <td>{formatFlightDateTime(booking.createdAt)}</td>
        <td>
          <Link
            to={ENDPOINT.MANAGER_FLIGHT_BOOKING_DETAIL(booking._id)}
            className={pageStyles.ActionLink}
          >
            Xem chi tiết
          </Link>
        </td>
      </tr>
    ));
  };

  return (
    <div className={`page-content ${pageStyles.Page}`}>
      <Meta name={PAGE_TITLE} />
      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />

        <Card className={pageStyles.ContentForm}>
          <form onSubmit={validation.handleSubmit}>
            <div className={pageStyles.FormWrapper}>
              <div className={pageStyles.SearchGroup}>
                <div className={pageStyles.FieldLabel}>Tìm kiếm</div>
                <div className={pageStyles.InputWrapper}>
                  <img src={SearchIcon} alt="search" className={pageStyles.SearchIcon} />
                  <AppInputValidation
                    name="q"
                    id="q"
                    type="text"
                    placeholder={FLIGHT_SEARCH_PLACEHOLDER}
                    validation={validation}
                    inputClassName={pageStyles.SearchInput}
                  />
                </div>
              </div>

              <div className={pageStyles.FilterGroup}>
                <div className={pageStyles.FieldLabel}>Bộ lọc</div>
                <div className={pageStyles.FilterInputsRow}>
                  <AppSelectValidation
                    name="status"
                    placeholder="Trạng thái"
                    options={FLIGHT_BOOKING_STATUS_OPTIONS}
                    value={values.status}
                    validation={validation}
                    onChange={onSelect}
                    selectedClassName={pageStyles.CommonSelect}
                    isHiddenSearchable
                  />
                  <AppSelectValidation
                    name="system"
                    placeholder="Hãng"
                    options={systemOptions}
                    value={values.system}
                    validation={validation}
                    onChange={onSelect}
                    selectedClassName={pageStyles.CommonSelect}
                    isHiddenSearchable
                  />
                  <AppSelectValidation
                    name="needsActionReason"
                    placeholder="Lý do cần xử lý"
                    options={NEEDS_ACTION_REASON_OPTIONS}
                    value={values.needsActionReason}
                    validation={validation}
                    onChange={onSelect}
                    selectedClassName={pageStyles.CommonSelect}
                    isHiddenSearchable
                  />
                  <AppFlatPickInput
                    name="createdRange"
                    placeholder="Ngày đặt"
                    className={pageStyles.CommonFlatpickr}
                    value={rangeLabel(values.createdFrom, values.createdTo)}
                    options={RANGE_OPTIONS}
                    onChange={onChangeRange('createdFrom', 'createdTo')}
                  />
                  <AppFlatPickInput
                    name="departureRange"
                    placeholder="Ngày bay"
                    className={pageStyles.CommonFlatpickr}
                    value={rangeLabel(values.departureFrom, values.departureTo)}
                    options={RANGE_OPTIONS}
                    onChange={onChangeRange('departureFrom', 'departureTo')}
                  />
                </div>
              </div>

              <div className={pageStyles.ButtonGroup}>
                <button type="button" className={pageStyles.BtnReset} onClick={onReset}>
                  Nhập lại
                </button>
                <button type="submit" className={pageStyles.BtnSubmit}>
                  Tìm kiếm
                </button>
              </div>
            </div>
          </form>
        </Card>

        <Card className={pageStyles.Content}>
          <div className="Tab__Header">
            <div className="Tab__Header__Scroll">
              {tabs.map((item) => (
                <div className="Tab__Header__Tab" key={item.value}>
                  <button
                    type="button"
                    onClick={() => pushQuery({ tab: item.value, page: 1 })}
                    className={`Tab__Header__Tab__Item ${tab === item.value ? 'Active' : ''}`}
                  >
                    {item.label}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="Table__Body">
            <Label className="Table__Body__Label">{`${list.total} đơn vé máy bay`}</Label>
            <div className={pageStyles.TableContainer}>
              <table className={pageStyles.Table}>
                <thead>
                  <tr>
                    {HEADS.map((head) => <th key={head}>{head}</th>)}
                  </tr>
                </thead>
                <tbody>{renderRows()}</tbody>
              </table>
            </div>
          </div>
        </Card>

        {list.data.length > 0 && (
          <Pagination
            page={page}
            pageCount={pageCount}
            onChangePage={({ selected }) => pushQuery({ page: selected + 1 })}
          />
        )}
      </Container>
    </div>
  );
}

export default ManagerFlightBooking;
